import React, { useState, useEffect, useCallback } from 'react';
import { findByIdentityWithManager } from '../../services/identity';
import { useNavigate } from 'react-router-dom';
import { Expense, ExpenseStatus, Receipt } from '../../types/expense';
import { ExpenseService } from '../../services/expense';
import { ExpenseServiceWrapper } from '../../services/expenseWrapper';
import { GraphQLExpenseService } from '../../services/graphqlExpense';
import { StorageService } from '../../services/storage';
import { CognitoAdminService } from '../../services/cognitoAdmin';
import { useAuth } from '../../contexts/AuthContext';
import { notifyExpenseSubmitted, notifyExpenseDeleted } from '../../services/inAppNotification';
import { emailExpenseSubmitted, emailExpenseDeleted } from '../../services/emailNotification';
import { getFinanceAdminEmails, getManagerEmailForUser, getUserByEmail } from '../../services/userLoader';
import './ExpenseDetail.css';

interface ReceiptWithUrl extends Receipt {
  displayUrl?: string;
  fileKey?: string;
}

interface ExpenseDetailProps {
  expense: Expense;
  onClose: () => void;
  onUpdate?: () => void;
}

interface ApproverInfo {
  name: string;
  email: string;
  roleLabel: string;
}

/**
 * Expense Detail Component with Receipt Display
 * Requirements: 6.1, 6.4 - Expense detail view with receipt display
 */
const ExpenseDetail: React.FC<ExpenseDetailProps> = ({ expense, onClose, onUpdate }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedReceipt, setSelectedReceipt] = useState<ReceiptWithUrl | null>(null);
  const [loading, setLoading] = useState(false);
  const [receipts, setReceipts] = useState<ReceiptWithUrl[]>(expense.receipts || []);
  const [loadingReceiptUrl, setLoadingReceiptUrl] = useState(false);
  const [loadingReceipts, setLoadingReceipts] = useState(true);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentApprover, setCurrentApprover] = useState<ApproverInfo | null>(null);
  const [approverLoading, setApproverLoading] = useState(false);

  // Allow receipt upload for the expense owner when expense is not finance-approved, rejected, or reimbursed
  const isOwner = (() => {
    const email = user?.email?.toLowerCase() || '';
    const userId = user?.id?.toLowerCase() || '';
    const submitter = expense.submitterId?.toLowerCase() || '';
    // Match by exact email, exact userId, or email prefix in Azure AD username
    if (submitter === email) return true;
    if (submitter === userId) return true;
    if (email && submitter.includes(email.split('@')[0])) return true;
    if (email && userId && userId.includes(email.split('@')[0]) && submitter === userId) return true;
    return false;
  })();

  const canUploadReceipt = isOwner &&
    expense.status !== ExpenseStatus.FINANCE_APPROVED &&
    expense.status !== ExpenseStatus.MANAGER_REJECTED &&
    expense.status !== ExpenseStatus.FINANCE_REJECTED &&
    expense.status !== ExpenseStatus.REIMBURSED;

  // Fetch receipts from database when component mounts
  useEffect(() => {
    const fetchReceipts = async () => {
      setLoadingReceipts(true);
      try {
        console.log('[ExpenseDetail] Fetching receipts for expense:', expense.id);

        let fetchedReceipts = await GraphQLExpenseService.getReceiptsForExpense(expense.id);
        console.log('[ExpenseDetail] Fetched receipts from DB:', fetchedReceipts.length);

        // Fallback: If no receipts in DynamoDB, check S3 directly
        if (!fetchedReceipts || fetchedReceipts.length === 0) {
          console.log('[ExpenseDetail] No DB receipts, checking S3 directly...');
          try {
            const s3Receipts = await StorageService.listReceiptsFromS3(expense.id);
            if (s3Receipts.length > 0) {
              console.log('[ExpenseDetail] Found', s3Receipts.length, 'receipts in S3');
              fetchedReceipts = s3Receipts;
              
              // Backfill DynamoDB records for future lookups
              for (const r of s3Receipts) {
                try {
                  await GraphQLExpenseService.createReceipt({
                    expenseId: r.expenseId,
                    fileName: r.fileName,
                    fileUrl: r.fileUrl,
                    fileKey: r.fileKey,
                    fileType: r.fileType,
                    fileSize: r.fileSize
                  });
                  console.log('[ExpenseDetail] Backfilled receipt record:', r.fileName);
                } catch (backfillErr) {
                  console.warn('[ExpenseDetail] Could not backfill receipt:', r.fileName, backfillErr);
                }
              }
            }
          } catch (s3Err) {
            console.warn('[ExpenseDetail] S3 fallback failed:', s3Err);
          }
        }

        if (fetchedReceipts && fetchedReceipts.length > 0) {
          // Map receipts and use fileUrl or fileKey
          const mappedReceipts: ReceiptWithUrl[] = fetchedReceipts.map((r: any) => ({
            id: r.id,
            expenseId: r.expenseId,
            fileName: r.fileName,
            fileUrl: r.fileUrl || '',
            fileKey: r.fileKey || '',
            fileType: r.fileType,
            fileSize: r.fileSize || 0,
            uploadedAt: r.uploadedAt
          }));
          setReceipts(mappedReceipts);
          console.log('[ExpenseDetail] Mapped receipts:', mappedReceipts.length);
        } else {
          console.log('[ExpenseDetail] No receipts found for expense:', expense.id);
          setReceipts([]);
        }
      } catch (error) {
        console.error('[ExpenseDetail] Error fetching receipts:', error);
        setReceipts([]);
      } finally {
        setLoadingReceipts(false);
      }
    };

    fetchReceipts();
  }, [expense.id]);

  const formatAmount = (amount: number): string => {
    return ExpenseService.formatAmount(amount);
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: ExpenseStatus): string => {
    return ExpenseService.getStatusColor(status);
  };

  const getStatusDescription = (status: ExpenseStatus): string => {
    return ExpenseService.getStatusDescription(status);
  };

  const isPendingApprovalStatus = (status: ExpenseStatus | string): boolean => {
    const normalized = String(status || '').trim().toLowerCase();
    return [
      ExpenseStatus.SUBMITTED.toLowerCase(),
      ExpenseStatus.MANAGER_APPROVED.toLowerCase(),
      'awaiting manager approval',
      'awaiting finance approval'
    ].includes(normalized);
  };

  const getCurrentApproverRoleLabel = (status: ExpenseStatus | string): string => {
    const normalized = String(status || '').trim().toLowerCase();
    if (normalized === ExpenseStatus.SUBMITTED.toLowerCase() || normalized === 'awaiting manager approval') {
      return 'Manager';
    }
    return 'Current Approver';
  };

  const extractPerson = (value: any): Partial<ApproverInfo> | null => {
    if (!value) return null;
    if (typeof value === 'string') {
      const text = value.trim();
      if (!text) return null;
      return text.includes('@') ? { email: text } : { name: text };
    }
    const name = value.name || value.fullName || value.displayName || value.employeeName || value.managerName || value.approverName;
    const email = value.email || value.employeeEmail || value.managerEmail || value.approverEmail || value.userEmail;
    if (!name && !email) return null;
    return { name, email };
  };

  const resolveUserByEmail = async (email?: string): Promise<Partial<ApproverInfo> | null> => {
    if (!email) return null;
    const matchedUser = await getUserByEmail(email);
    return {
      name: matchedUser?.name || email,
      email
    };
  };

  const getSubmitterEmail = useCallback((): string | null => {
    const expenseAny = expense as any;
    const candidates = [
      expenseAny.submitterEmail,
      expenseAny.employeeEmail,
      expenseAny.userEmail,
      expense.submitterId
    ];
    const email = candidates.find(value => typeof value === 'string' && value.includes('@'));
    return email || null;
  }, [expense]);

  const getApproverCandidate = useCallback((submitter: any): any => {
    const expenseAny = expense as any;
    const approvalWorkflow = expenseAny.approvalWorkflow || expenseAny.workflow || {};
    const employee = submitter || expenseAny.employee || {};

    const approver =
      expenseAny.manager ||
      expenseAny.managerDetails ||
      expenseAny.reportsTo ||
      employee.manager ||
      approvalWorkflow.currentApprover;

    return approver || expenseAny.currentApprover || expenseAny.pendingApprover;
  }, [expense]);

  useEffect(() => {
    let isMounted = true;

    const resolveCurrentApprover = async () => {
      if (!isPendingApprovalStatus(expense.status)) {
        setCurrentApprover(null);
        return;
      }

      setApproverLoading(true);
      try {
        const roleLabel = getCurrentApproverRoleLabel(expense.status);
        const submitterEmail = getSubmitterEmail();
        const submitter = submitterEmail ? await getUserByEmail(submitterEmail) : null;
        let approver = extractPerson(getApproverCandidate(submitter));

        if ((!approver || (!approver.name && !approver.email)) && roleLabel === 'Manager' && submitterEmail) {
          const managerEmail = await getManagerEmailForUser(submitterEmail);
          approver = await resolveUserByEmail(managerEmail || undefined);
        }

        if ((!approver || (!approver.name && !approver.email)) && roleLabel !== 'Manager') {
          const financeEmails = await getFinanceAdminEmails();
          if (financeEmails.length === 1) {
            approver = await resolveUserByEmail(financeEmails[0]);
          } else if (financeEmails.length > 1) {
            approver = {
              name: financeEmails.join(', '),
              email: financeEmails.join(',')
            };
          }
        }

        if (approver?.email && !approver.name) {
          approver = await resolveUserByEmail(approver.email);
        }

        if (!isMounted) return;
        if (approver?.name || approver?.email) {
          setCurrentApprover({
            name: approver.name || approver.email || '',
            email: approver.email || '',
            roleLabel
          });
        } else {
          setCurrentApprover(null);
        }
      } catch (error) {
        console.warn('[ExpenseDetail] Could not resolve current approver:', error);
        if (isMounted) setCurrentApprover(null);
      } finally {
        if (isMounted) setApproverLoading(false);
      }
    };

    resolveCurrentApprover();
    return () => {
      isMounted = false;
    };
  }, [expense, getApproverCandidate, getSubmitterEmail]);

  const handleEdit = () => {
    navigate(`/expenses/${expense.id}/edit`);
    onClose();
  };

  const handleDelete = async () => {
    const confirmMsg = expense.status === ExpenseStatus.SUBMITTED
      ? 'This expense has been submitted. Are you sure you want to delete it? Your manager will be notified.'
      : 'Are you sure you want to delete this expense? This action cannot be undone.';
    if (window.confirm(confirmMsg)) {
      try {
        setLoading(true);
        const wasSubmitted = expense.status === ExpenseStatus.SUBMITTED;
        await ExpenseServiceWrapper.deleteDraft(expense.id);

        // Notify manager if the expense was in Submitted status
        if (wasSubmitted) {
          let managerEmail = user?.managerEmail;
          if (!managerEmail && user?.email && CognitoAdminService.isConfigured()) {
            try {
              const response = await CognitoAdminService.listUsers();
              const cognitoUser = findByIdentityWithManager(response.users, user?.email);
              if (cognitoUser?.managerEmail) {
                managerEmail = cognitoUser.managerEmail;
              }
            } catch (err) {
              console.warn('[ExpenseDetail] Could not fetch manager email:', err);
            }
          }
          if (managerEmail) {
            const employeeName = user?.name || 'Employee';
            try {
              await notifyExpenseDeleted(managerEmail, employeeName, expense.title, expense.amount);
              await emailExpenseDeleted(managerEmail, employeeName, expense.title, expense.amount);
              console.log('[ExpenseDetail] Manager notified about expense deletion');
            } catch (notifyErr) {
              console.warn('[ExpenseDetail] Could not notify manager about deletion:', notifyErr);
            }
          }
        }

        if (onUpdate) {
          onUpdate();
        }
        onClose();
      } catch (error) {
        console.error('Failed to delete expense:', error);
        alert('Failed to delete expense. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSubmit = async () => {
    const validation = ExpenseService.validateForSubmission(expense);

    if (!validation.isValid) {
      alert(`Cannot submit expense:\n${validation.errors.join('\n')}`);
      return;
    }

    if (window.confirm('Are you sure you want to submit this expense for approval?')) {
      try {
        setLoading(true);

        // Get manager email - if not in user context, fetch from Cognito Admin API
        let managerEmail = user?.managerEmail;
        if (!managerEmail && user?.email && CognitoAdminService.isConfigured()) {
          try {
            console.log('[ExpenseDetail] Fetching manager email from Cognito for:', user.email);
            const response = await CognitoAdminService.listUsers();
            const cognitoUser = findByIdentityWithManager(response.users, user?.email);
            if (cognitoUser?.managerEmail) {
              managerEmail = cognitoUser.managerEmail;
              console.log('[ExpenseDetail] Found manager email:', managerEmail);
            }
          } catch (err) {
            console.warn('[ExpenseDetail] Could not fetch manager email from Cognito:', err);
          }
        }

        // Pass user info for workflow routing (bypass manager if no manager assigned)
        const updatedExpense = await ExpenseServiceWrapper.submitForApproval(expense.id, {
          role: user?.role,
          managerEmail: managerEmail
        });

        // Send notifications based on the new status
        const employeeName = user?.name || 'Employee';
        
        if (updatedExpense.status === ExpenseStatus.SUBMITTED && managerEmail) {
          // Expense needs manager approval - notify manager
          console.log('[ExpenseDetail] Sending notification to manager:', managerEmail);
          
          // In-app notification to manager
          await notifyExpenseSubmitted(
            managerEmail,
            employeeName,
            expense.title,
            expense.amount,
            expense.id
          );
          
          // Email notification to manager
          await emailExpenseSubmitted(
            managerEmail,
            employeeName,
            expense.title,
            expense.amount,
            expense.id
          );
        } else if (updatedExpense.status === ExpenseStatus.MANAGER_APPROVED) {
          // Expense bypassed manager (user is manager/admin or has no manager)
          // Notify finance admins directly
          console.log('[ExpenseDetail] Expense bypassed manager, notifying finance admins');
          
          try {
            const financeAdminEmails = await getFinanceAdminEmails();
            console.log('[ExpenseDetail] Finance admin emails:', financeAdminEmails);
            
            // Send in-app notifications to finance admins
            for (const adminEmail of financeAdminEmails) {
              await notifyExpenseSubmitted(
                adminEmail,
                employeeName,
                expense.title,
                expense.amount,
                expense.id
              );
            }
            
            // Send email to finance admins (emailExpenseManagerApproved handles this)
            if (financeAdminEmails.length > 0) {
              const { emailExpenseManagerApproved } = await import('../../services/emailNotification');
              await emailExpenseManagerApproved(
                user?.email || '',
                financeAdminEmails,
                'System',
                expense.title,
                expense.amount,
                expense.id
              );
            }
          } catch (notifError) {
            console.warn('[ExpenseDetail] Could not send finance admin notifications:', notifError);
          }
        }

        if (onUpdate) {
          onUpdate();
        }
        onClose();
      } catch (error) {
        console.error('Failed to submit expense:', error);
        alert('Failed to submit expense. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const openReceiptViewer = async (receipt: ReceiptWithUrl) => {
    setLoadingReceiptUrl(true);
    try {
      // Get a fresh pre-signed URL for viewing
      const s3Key = receipt.fileKey || extractS3Key(receipt.fileUrl);
      if (s3Key) {
        const freshUrl = await StorageService.getReceiptUrl(s3Key);
        setSelectedReceipt({ ...receipt, displayUrl: freshUrl });
      } else {
        // Fallback to stored URL
        setSelectedReceipt({ ...receipt, displayUrl: receipt.fileUrl });
      }
    } catch (error) {
      console.error('[ExpenseDetail] Error getting receipt URL:', error);
      // Fallback to stored URL
      setSelectedReceipt({ ...receipt, displayUrl: receipt.fileUrl });
    } finally {
      setLoadingReceiptUrl(false);
    }
  };

  // Extract S3 key from URL - returns key WITHOUT 'public/' prefix
  // because Amplify Storage v6 adds 'public/' automatically
  const extractS3Key = (url: string): string | null => {
    if (!url) return null;
    try {
      console.log('[ExpenseDetail] Extracting S3 key from URL:', url.substring(0, 80) + '...');

      // Handle public/ prefix that Amplify adds - extract the path after public/
      if (url.includes('public/')) {
        const match = url.match(/public\/(.+?)(\?|$)/);
        if (match) {
          console.log('[ExpenseDetail] Extracted key (without public/):', match[1]);
          return match[1]; // Return WITHOUT 'public/' - Amplify adds it
        }
      }
      // Handle receipts/ path directly
      if (url.includes('receipts/')) {
        const match = url.match(/(receipts\/.+?)(\?|$)/);
        if (match) {
          console.log('[ExpenseDetail] Extracted key:', match[1]);
          return match[1];
        }
      }
      return null;
    } catch {
      return null;
    }
  };

  const closeReceiptViewer = () => {
    setSelectedReceipt(null);
  };

  const downloadReceipt = async (receipt: ReceiptWithUrl) => {
    try {
      // Get a fresh pre-signed URL for download
      const s3Key = receipt.fileKey || extractS3Key(receipt.fileUrl);
      let downloadUrl = receipt.fileUrl;

      if (s3Key) {
        downloadUrl = await StorageService.getReceiptUrl(s3Key);
      }

      // Fetch the file as a blob to force download instead of opening in tab
      const response = await fetch(downloadUrl);
      const blob = await response.blob();

      // Create a blob URL and trigger download
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = receipt.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('[ExpenseDetail] Error downloading receipt:', error);
      alert('Failed to download receipt. Please try again.');
    }
  };

  const handleReceiptUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploadingReceipt(true);
    setUploadProgress(0);

    const totalFiles = files.length;
    let uploaded = 0;

    for (const file of Array.from(files)) {
      try {
        // Validate file
        const validation = StorageService.validateFile(file);
        if (!validation.isValid) {
          alert(`${file.name}: ${validation.error}`);
          continue;
        }

        // Upload to S3
        console.log('[ExpenseDetail] Uploading receipt:', file.name);
        const receipt = await StorageService.uploadReceipt(expense.id, file, (progress) => {
          setUploadProgress(Math.round(((uploaded + progress / 100) / totalFiles) * 100));
        });

        // Save receipt record to DynamoDB
        const savedReceipt = await GraphQLExpenseService.createReceipt({
          expenseId: expense.id,
          fileName: receipt.fileName,
          fileUrl: receipt.fileUrl,
          fileKey: receipt.fileKey,
          fileType: receipt.fileType,
          fileSize: receipt.fileSize
        });
        console.log('[ExpenseDetail] Receipt saved:', savedReceipt?.id);

        // Add to local state
        const newReceipt: ReceiptWithUrl = {
          id: savedReceipt?.id || receipt.id,
          expenseId: expense.id,
          fileName: receipt.fileName,
          fileUrl: receipt.fileUrl,
          fileKey: receipt.fileKey || '',
          fileType: receipt.fileType,
          fileSize: receipt.fileSize,
          uploadedAt: receipt.uploadedAt
        };
        setReceipts(prev => [...prev, newReceipt]);

        uploaded++;
        setUploadProgress(Math.round((uploaded / totalFiles) * 100));
      } catch (err) {
        console.error('[ExpenseDetail] Failed to upload receipt:', file.name, err);
        alert(`Failed to upload ${file.name}. Please try again.`);
      }
    }

    setUploadingReceipt(false);
    setUploadProgress(0);

    // Reset file input
    event.target.value = '';

    if (uploaded > 0 && onUpdate) {
      onUpdate();
    }
  };

  return (
    <div className="expense-detail">
      <div className="expense-detail-header">
        <h2>Expense Details</h2>
        <button className="close-button" onClick={onClose} disabled={loading}>
          ×
        </button>
      </div>

      <div className="expense-detail-content">
        {/* Basic Information */}
        <div className="detail-section">
          <div className="section-header">
            <h3>Basic Information</h3>
            <div className={`expense-status ${getStatusColor(expense.status)}`}>
              {expense.status}
            </div>
          </div>

          <div className="detail-grid">
            <div className="detail-item">
              <label>Title:</label>
              <span>{expense.title}</span>
            </div>

            <div className="detail-item">
              <label>Amount:</label>
              <span className="amount">{formatAmount(expense.amount)}</span>
            </div>

            <div className="detail-item">
              <label>Category:</label>
              <span className="category">{expense.category}</span>
            </div>

            <div className="detail-item">
              <label>Date:</label>
              <span>{formatDate(expense.date)}</span>
            </div>

            <div className="detail-item">
              <label>Payment Method:</label>
              <span>{expense.paymentMethod}</span>
            </div>

            <div className="detail-item">
              <label>Status:</label>
              <span>{getStatusDescription(expense.status)}</span>
            </div>
          </div>

          {isPendingApprovalStatus(expense.status) && (
            <div className="approver-info">
              <h4>Approver Information</h4>
              {approverLoading ? (
                <p className="approver-unavailable">Loading approver information...</p>
              ) : currentApprover ? (
                <div className="approver-grid">
                  <div className="approver-field">
                    <label>{currentApprover.roleLabel}:</label>
                    <span>{currentApprover.name}</span>
                  </div>
                  <div className="approver-field">
                    <label>Email:</label>
                    {currentApprover.email ? (
                      <a href={`mailto:${currentApprover.email}`}>{currentApprover.email}</a>
                    ) : (
                      <span>Approver information unavailable</span>
                    )}
                  </div>
                </div>
              ) : (
                <p className="approver-unavailable">Approver information unavailable</p>
              )}
            </div>
          )}

          {expense.description && (
            <div className="detail-item full-width">
              <label>Description:</label>
              <p className="description">{expense.description}</p>
            </div>
          )}
        </div>

        {/* Receipts */}
        <div className="detail-section">
          <h3>Receipts ({loadingReceipts ? '...' : receipts?.length || 0})</h3>

          {loadingReceipts ? (
            <div className="no-receipts">
              <p>Loading receipts...</p>
            </div>
          ) : receipts && receipts.length > 0 ? (
            <div className="receipts-grid">
              {receipts.map(receipt => (
                <div key={receipt.id} className="receipt-item">
                  <div className="receipt-info">
                    <div className="receipt-name">{receipt.fileName}</div>
                    <div className="receipt-meta">
                      <span className="receipt-type">{receipt.fileType}</span>
                      <span className="receipt-size">
                        {(receipt.fileSize / 1024).toFixed(1)} KB
                      </span>
                      <span className="receipt-date">
                        {formatDateTime(receipt.uploadedAt)}
                      </span>
                    </div>
                  </div>

                  <div className="receipt-actions">
                    <button
                      className="receipt-action-button view"
                      onClick={() => openReceiptViewer(receipt)}
                    >
                      View
                    </button>
                    <button
                      className="receipt-action-button download"
                      onClick={() => downloadReceipt(receipt)}
                    >
                      Download
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-receipts">
              <p>No receipts uploaded for this expense.</p>
            </div>
          )}

          {/* Upload Receipt Section */}
          {canUploadReceipt && (
            <div className="receipt-upload-section">
              <label className="receipt-upload-label" htmlFor="receipt-upload-input">
                <span className="upload-icon">📎</span>
                <span className="upload-text">
                  {uploadingReceipt
                    ? `Uploading... ${uploadProgress}%`
                    : 'Add Receipt (PDF, JPG, PNG — max 10MB)'}
                </span>
                {uploadingReceipt && (
                  <div className="upload-progress-bar">
                    <div className="upload-progress-fill" style={{ width: `${uploadProgress}%` }} />
                  </div>
                )}
              </label>
              <input
                id="receipt-upload-input"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                multiple
                onChange={handleReceiptUpload}
                disabled={uploadingReceipt || loading}
                style={{ display: 'none' }}
              />
            </div>
          )}
        </div>

        {/* Approval History & Waiting Status */}
        <div className="detail-section">
          <h3>Approval Tracking</h3>

          {/* Waiting On indicator */}
          {expense.status === ExpenseStatus.SUBMITTED && (
            <div className="waiting-on-banner waiting-manager">
              ⏳ Waiting on: <strong>Manager Approval</strong>
            </div>
          )}
          {expense.status === ExpenseStatus.MANAGER_APPROVED && (
            <div className="waiting-on-banner waiting-finance">
              ⏳ Waiting on: <strong>Finance Approval</strong>
            </div>
          )}
          {expense.status === ExpenseStatus.FINANCE_APPROVED && (
            <div className="waiting-on-banner waiting-reimburse">
              ⏳ Waiting on: <strong>Reimbursement</strong>
            </div>
          )}
          {(expense.status === ExpenseStatus.REIMBURSED) && (
            <div className="waiting-on-banner waiting-complete">
              ✅ <strong>Workflow Complete</strong> — Reimbursed
            </div>
          )}
          {(expense.status === ExpenseStatus.MANAGER_REJECTED || expense.status === ExpenseStatus.FINANCE_REJECTED) && (
            <div className="waiting-on-banner waiting-rejected">
              ❌ <strong>Rejected</strong>
            </div>
          )}

          {expense.approvals && expense.approvals.length > 0 ? (
            <div className="approval-timeline">
              {expense.approvals
                .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
                .map(approval => (
                  <div key={approval.id} className="approval-item">
                    <div className="approval-header">
                      <span className={`approval-status ${approval.status.toLowerCase()}`}>
                        {approval.status === 'Approved' ? '✅' : '❌'} {approval.status}
                      </span>
                      <span className="approval-level">
                        {approval.level === 1 ? '👔 Manager' : '💰 Finance'}
                      </span>
                      <span className="approval-date">
                        {formatDateTime(approval.timestamp)}
                      </span>
                    </div>
                    <div className="approval-approver">
                      Actioned by: <strong>{approval.approverId}</strong>
                    </div>
                    {approval.comments && (
                      <div className="approval-comments">
                        <strong>Comments:</strong> {approval.comments}
                      </div>
                    )}
                  </div>
                ))}
            </div>
          ) : (
            <p style={{ color: '#94a3b8', fontSize: '13px', fontStyle: 'italic' }}>No approval actions yet.</p>
          )}
        </div>

        {/* Metadata */}
        <div className="detail-section">
          <h3>Metadata</h3>

          <div className="detail-grid">
            <div className="detail-item">
              <label>Created:</label>
              <span>{formatDateTime(expense.createdAt)}</span>
            </div>

            <div className="detail-item">
              <label>Last Updated:</label>
              <span>{formatDateTime(expense.updatedAt)}</span>
            </div>

            <div className="detail-item">
              <label>Expense ID:</label>
              <span className="expense-id">{expense.id}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="expense-detail-actions">
        {ExpenseService.canEdit(expense) && (
          <button
            className="action-button edit"
            onClick={handleEdit}
            disabled={loading}
          >
            Edit Expense
          </button>
        )}

        {ExpenseService.canSubmit(expense) && (
          <button
            className="action-button submit"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Submitting...' : 'Submit for Approval'}
          </button>
        )}

        {ExpenseService.canDelete(expense) && (
          <button
            className="action-button delete"
            onClick={handleDelete}
            disabled={loading}
          >
            {loading ? 'Deleting...' : 'Delete Expense'}
          </button>
        )}
      </div>

      {/* Receipt Viewer Modal */}
      {selectedReceipt && (
        <div className="receipt-viewer-modal">
          <div className="receipt-viewer-backdrop" onClick={closeReceiptViewer} />
          <div className="receipt-viewer-content">
            <div className="receipt-viewer-header">
              <h3>{selectedReceipt.fileName}</h3>
              <button className="close-button" onClick={closeReceiptViewer}>
                ×
              </button>
            </div>

            <div className="receipt-viewer-body">
              {loadingReceiptUrl ? (
                <div className="loading-receipt">Loading receipt...</div>
              ) : selectedReceipt.fileType.toLowerCase().includes('pdf') ? (
                <iframe
                  src={selectedReceipt.displayUrl || selectedReceipt.fileUrl}
                  title={selectedReceipt.fileName}
                  className="receipt-pdf-viewer"
                />
              ) : (
                <img
                  src={selectedReceipt.displayUrl || selectedReceipt.fileUrl}
                  alt={selectedReceipt.fileName}
                  className="receipt-image-viewer"
                />
              )}
            </div>

            <div className="receipt-viewer-actions">
              <button
                className="action-button download"
                onClick={() => downloadReceipt(selectedReceipt)}
              >
                Download
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseDetail;
